import type { SvgIconProps } from '@mui/material';
import { Box } from '@mui/material';
import React from 'react';

import * as Flags from './flags';

/**
 * Resolves a bundled flag icon by ISO country code. Flags are exported in
 * PascalCase (e.g. "IS" -> `IsIcon`); territories without a bundled flag
 * resolve to `undefined`.
 */
export const flagFor = (code: string) => {
  // eslint-disable-next-line sonarjs/null-dereference -- code is typed as a non-null string
  const name = `${code.charAt(0).toUpperCase()}${code
    .slice(1)
    .toLowerCase()}Icon`;
  return (Flags as Record<string, React.ComponentType<SvgIconProps>>)[name];
};

export type CountryFlagProps = {
  /** ISO 3166-1 alpha-2 country code (case-insensitive, e.g. "IS"). */
  code: string;
  /** Width and height in pixels. */
  size?: number;
} & SvgIconProps;

/**
 * Renders a circular flag for the given country code. Territories without a
 * bundled flag fall back to a neutral placeholder of the same size.
 */
export const CountryFlag = ({
  code,
  size = 20,
  sx,
  ...props
}: CountryFlagProps) => {
  const Flag = flagFor(code);
  if (Flag) {
    return (
      <Flag
        sx={{ width: size, height: size, flexShrink: 0, ...sx }}
        {...props}
      />
    );
  }
  return (
    <Box
      sx={{
        height: size,
        width: size,
        borderRadius: '50%',
        bgcolor: 'action.disabled',
        flexShrink: 0,
        ...sx,
      }}
    />
  );
};
